<template>
  <div class="noble-page" :class="{ embedded: embedded }">
    <div class="container">
      <!-- 页面标题 -->
      <div class="page-header">
        <div class="title-lines">
          <div class="line left"></div>
          <div class="title-text">
            <h2>灵眸云图贵族 <span>尊享特权</span></h2>
          </div>
          <div class="line right"></div>
        </div>
      </div>

      <!-- 当前贵族 -->
      <div v-if="currentNoble" class="current-noble">
        <img :src="getBadge(currentNoble)" class="current-badge" alt="">
        <div class="current-info">
          <div class="current-label">当前贵族身份</div>
          <div class="current-name">{{ currentNoble.level_name }}</div>
          <div class="current-time">有效期至：{{ currentNoble.expire_time ? formatTime(currentNoble.expire_time) : '永久' }}</div>
        </div>
      </div>

      <!-- 加载 -->
      <div v-if="loading" class="loading">
        <div class="spinner"></div>
        <span>加载中...</span>
      </div>

      <template v-else>
        <!-- 等级图标列表 -->
        <div class="level-list">
          <div
            v-for="(item, index) in nobleLevels"
            :key="item.level"
            class="level-item"
            :class="{ active: selectedIndex === index, owned: item.owned }"
            @click="selectedIndex = index"
          >
            <div class="level-icon-wrap">
              <img :src="getBadge(item)" alt="">
              <div v-if="item.owned" class="owned-dot">已开通</div>
            </div>
            <div class="level-name">{{ item.name }}</div>
          </div>
        </div>

        <!-- 开通福利 -->
        <div class="welfare-box">
          <div class="welfare-title">开通福利</div>
          <div class="welfare-cards">
            <div class="welfare-card">
              <div class="welfare-label">开通即送</div>
              <div class="welfare-value"><b>价值{{ Math.round(activeItem.price * 0.3) }}</b> 元礼物券</div>
              <div class="welfare-desc">可用于直播间送礼</div>
            </div>
            <div class="welfare-card hot">
              <div class="welfare-tag">限时特惠</div>
              <div class="welfare-label">续费专享</div>
              <div class="welfare-value"><b>9</b> 折优惠</div>
              <div class="welfare-desc">到期后续费自动生效</div>
            </div>
          </div>
        </div>

        <!-- 特权列表 -->
        <div class="privilege-box">
          <div class="privilege-title">
            <span class="pt-line"></span>
            尊享 {{ (activeItem.privileges || []).length || defaultPrivileges.length }} 项特权
            <span class="pt-line"></span>
          </div>
          <div class="privilege-list">
            <div
              v-for="(priv, idx) in (activeItem.privileges || defaultPrivileges)"
              :key="idx"
              class="privilege-row"
            >
              <span class="privilege-tag">特权{{ idx + 1 }}</span>
              <span class="privilege-text">{{ priv }}</span>
            </div>
          </div>
        </div>

        <!-- 底部开通按钮 -->
        <div class="buy-bar">
          <div class="buy-info">
            <img :src="getBadge(activeItem)" alt="">
            <div>
              <div class="buy-name">{{ activeItem.name }}</div>
              <div class="buy-price">¥{{ activeItem.price }} / {{ activeItem.duration_days || 30 }}天</div>
            </div>
          </div>
          <button v-if="activeItem.owned" class="buy-btn owned" disabled>当前身份</button>
          <button v-else class="buy-btn" @click="openBuyConfirm(activeItem)">开通{{ activeItem.name }}</button>
        </div>
      </template>
    </div>

    <!-- 开通确认 -->
    <div v-if="showConfirm" class="modal-overlay" @click="showConfirm = false">
      <div class="modal-content" @click.stop>
        <img :src="getBadge(confirmNoble)" class="modal-badge" alt="">
        <h3>开通 {{ confirmNoble.name }}</h3>
        <p class="modal-price">¥{{ confirmNoble.price }} / {{ confirmNoble.duration_days || 30 }}天</p>
        <div class="modal-balance">
          <span>当前余额</span>
          <b>¥{{ balance || 0 }}</b>
          <span v-if="balance < confirmNoble.price" class="insufficient">余额不足</span>
        </div>
        <div class="modal-actions">
          <button class="cancel" @click="showConfirm = false">取消</button>
          <button class="confirm" :disabled="balance < confirmNoble.price || buying" @click="handleBuy">
            {{ buying ? '开通中...' : '确认开通' }}
          </button>
        </div>
        <p v-if="balance < confirmNoble.price" class="recharge-tip">
          余额不足，请 <router-link :to="{ path: '/user', query: { tab: 'recharge' } }">去充值</router-link>
        </p>
      </div>
    </div>

    <PageFooter v-if="!$props.embedded" />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import PageFooter from './Footer.vue';
import { getMyNoble, buyNoble } from '../api/noble';
import { BASE_URL } from '../api/config';

const badgeMap = {
  1: require('../assets/noble/tier1.jpg'),
  2: require('../assets/noble/tier2.jpg'),
  3: require('../assets/noble/tier3.jpg'),
  4: require('../assets/noble/tier4.jpg'),
  5: require('../assets/noble/tier5.jpg'),
  6: require('../assets/noble/tier6.jpg'),
};

const defaultPrivileges = [
  '专属贵族身份徽章',
  '进场专属特效',
  '贵族专属气泡框',
  '直播间防禁言',
  '专属客服通道',
  '生日专属礼包'
];

export default {
  name: 'NobleShop',
  components: { PageFooter },
  props: {
    embedded: { type: Boolean, default: false }
  },
  setup() {
    const loading = ref(false);
    const currentNoble = ref(null);
    const nobleLevels = ref([]);
    const selectedIndex = ref(0);
    const balance = ref(null);
    const showConfirm = ref(false);
    const confirmNoble = ref({});
    const buying = ref(false);

    const activeItem = computed(() => nobleLevels.value[selectedIndex.value] || {});

    const fetchData = async () => {
      loading.value = true;
      try {
        const res = await getMyNoble();
        if (res.code === 1) {
          const data = res.data;
          currentNoble.value = data.current_noble || null;
          nobleLevels.value = data.levels || [];
          balance.value = data.balance || 0;

          if (currentNoble.value && nobleLevels.value.length) {
            const idx = nobleLevels.value.findIndex(l => l.level === currentNoble.value.level);
            selectedIndex.value = idx >= 0 ? idx : nobleLevels.value.length - 1;
          } else if (nobleLevels.value.length) {
            selectedIndex.value = nobleLevels.value.length - 1;
          }
        }
      } catch (e) {
        console.error('获取贵族信息失败', e);
      } finally {
        loading.value = false;
      }
    };

    const getFullImageUrl = (url) => {
      if (!url) return '';
      if (url.startsWith('http') || url.startsWith('data:')) return url;
      return `${BASE_URL}${url}`;
    };

    const getBadge = (item) => {
      const level = typeof item === 'object' ? item?.level : item;
      const icon = typeof item === 'object' ? item?.icon : null;
      if (icon) return getFullImageUrl(icon);
      return badgeMap[level] || badgeMap[1];
    };

    const openBuyConfirm = (item) => {
      confirmNoble.value = item;
      showConfirm.value = true;
    };

    const handleBuy = async () => {
      if (balance.value < confirmNoble.value.price) {
        alert('余额不足，请先充值');
        return;
      }
      buying.value = true;
      try {
        const res = await buyNoble(confirmNoble.value.level);
        if (res.code === 1) {
          alert('开通成功！');
          showConfirm.value = false;
          await fetchData();
        } else {
          alert(res.msg || '开通失败');
        }
      } catch (e) {
        alert('开通失败，请重试');
      } finally {
        buying.value = false;
      }
    };

    const formatTime = (ts) => {
      if (!ts) return '';
      const num = Number(ts);
      if (isNaN(num)) return ts;
      const d = new Date(num * 1000);
      if (isNaN(d.getTime())) return ts;
      return d.toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
    };

    onMounted(fetchData);

    return {
      loading,
      currentNoble,
      nobleLevels,
      selectedIndex,
      activeItem,
      balance,
      showConfirm,
      confirmNoble,
      buying,
      defaultPrivileges,
      getBadge,
      openBuyConfirm,
      handleBuy,
      formatTime
    };
  }
};
</script>

<style scoped>
.noble-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #13131f 0%, #0d0d16 100%);
  padding: 32px 0 0;
  color: #fff;
}
.noble-page.embedded {
  min-height: auto;
  padding: 0;
  background: linear-gradient(180deg, #13131f 0%, #0d0d16 100%);
  border-radius: 12px;
  overflow: hidden;
}
.noble-page.embedded .container {
  max-width: none;
  margin: 0;
  padding: 24px;
}

.container {
  max-width: 980px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 标题 */
.page-header {
  margin-bottom: 24px;
}
.title-lines {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}
.title-lines .line {
  height: 2px;
  width: 80px;
  background: linear-gradient(90deg, transparent, rgba(255, 150, 0, 0.6), transparent);
}
.title-text h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #ff9600;
  letter-spacing: 1px;
}
.title-text h2 span {
  color: #fff;
}

/* 当前贵族 */
.current-noble {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  border-radius: 14px;
  background: linear-gradient(90deg, rgba(255, 150, 0, 0.12), rgba(255, 150, 0, 0.03));
  border: 1px solid rgba(255, 150, 0, 0.2);
  margin-bottom: 24px;
}
.current-badge {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 150, 0, 0.4);
}
.current-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
}
.current-name {
  font-size: 18px;
  font-weight: 800;
  color: #ffcc80;
  margin: 2px 0;
}
.current-time {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
}

/* 等级列表 */
.level-list {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-bottom: 28px;
  flex-wrap: wrap;
}
.level-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  opacity: 0.55;
  transition: all 0.25s ease;
}
.level-item:hover,
.level-item.active {
  opacity: 1;
  transform: translateY(-4px);
}
.level-item.active .level-icon-wrap {
  box-shadow: 0 0 0 3px rgba(255, 150, 0, 0.5), 0 8px 24px rgba(255, 150, 0, 0.25);
}
.level-item.active .level-name {
  color: #ffcc80;
  font-weight: 700;
}
.level-icon-wrap {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.08);
  transition: all 0.25s ease;
}
.level-icon-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.owned-dot {
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
}
.level-name {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.75);
  transition: all 0.25s ease;
}

/* 开通福利 */
.welfare-box {
  border: 1px solid rgba(255, 150, 0, 0.25);
  border-radius: 16px;
  padding: 20px;
  background: linear-gradient(180deg, rgba(255, 150, 0, 0.08), rgba(255, 150, 0, 0.02));
  margin-bottom: 24px;
  position: relative;
}
.welfare-box::before,
.welfare-box::after {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  border: 2px solid rgba(255, 150, 0, 0.5);
}
.welfare-box::before { top: -1px; left: -1px; border-right: none; border-bottom: none; }
.welfare-box::after { bottom: -1px; right: -1px; border-left: none; border-top: none; }

.welfare-title {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: #13131f;
  padding: 0 16px;
  font-size: 15px;
  font-weight: 700;
  color: #ff9600;
}
.welfare-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.welfare-card {
  background: rgba(0, 0, 0, 0.35);
  border-radius: 12px;
  padding: 18px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.welfare-card.hot {
  border-color: rgba(255, 106, 0, 0.35);
  background: linear-gradient(180deg, rgba(255, 106, 0, 0.12), rgba(255, 106, 0, 0.03));
  position: relative;
}
.welfare-tag {
  position: absolute;
  top: -1px;
  right: -1px;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 0 12px 0 12px;
}
.welfare-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 6px;
}
.welfare-value {
  font-size: 14px;
  color: #fff;
  margin-bottom: 4px;
}
.welfare-value b {
  font-size: 26px;
  font-weight: 800;
  color: #ffcc80;
}
.welfare-desc {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
}

/* 特权列表 */
.privilege-box {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.02);
  margin-bottom: 24px;
}
.privilege-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 17px;
  font-weight: 800;
  color: #ff9600;
  margin-bottom: 20px;
}
.pt-line {
  flex: 1;
  max-width: 80px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 150, 0, 0.5), transparent);
}
.privilege-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}
.privilege-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
}
.privilege-tag {
  flex-shrink: 0;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
}
.privilege-text {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
}

/* 底部开通 */
.buy-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-radius: 16px;
  background: linear-gradient(90deg, rgba(255, 150, 0, 0.1), rgba(255, 150, 0, 0.02));
  border: 1px solid rgba(255, 150, 0, 0.18);
  margin-bottom: 40px;
}
.buy-info {
  display: flex;
  align-items: center;
  gap: 12px;
}
.buy-info img {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 150, 0, 0.35);
}
.buy-name {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
}
.buy-price {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
}
.buy-btn {
  height: 46px;
  padding: 0 32px;
  border-radius: 24px;
  border: none;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  color: #fff;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(255, 106, 0, 0.35);
  transition: all 0.25s ease;
}
.buy-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(255, 106, 0, 0.5);
}
.buy-btn.owned {
  background: rgba(255, 255, 255, 0.1);
  box-shadow: none;
  cursor: not-allowed;
}

/* 弹窗 */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-content {
  background: linear-gradient(180deg, #1a1a26, #101018);
  border: 1px solid rgba(255, 150, 0, 0.25);
  border-radius: 20px;
  padding: 32px;
  width: 90%;
  max-width: 380px;
  text-align: center;
}
.modal-badge {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(255, 150, 0, 0.4);
  margin-bottom: 14px;
}
.modal-content h3 {
  margin: 0 0 8px;
  font-size: 20px;
  color: #fff;
}
.modal-price {
  font-size: 24px;
  font-weight: 800;
  color: #ffcc80;
  margin: 0 0 20px;
}
.modal-balance {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  margin-bottom: 20px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
}
.modal-balance b {
  color: #fff;
  font-size: 18px;
}
.insufficient {
  color: #ff5a5a;
  font-size: 12px;
  font-weight: 700;
}
.modal-actions {
  display: flex;
  gap: 12px;
}
.modal-actions button {
  flex: 1;
  height: 44px;
  border-radius: 10px;
  border: none;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
}
.modal-actions .cancel {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
}
.modal-actions .confirm {
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  color: #fff;
}
.modal-actions .confirm:disabled {
  background: #444;
  cursor: not-allowed;
}
.recharge-tip {
  margin: 14px 0 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
}
.recharge-tip a {
  color: #ff9600;
  text-decoration: none;
  font-weight: 700;
}

/* 加载 */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 80px 20px;
  color: rgba(255, 255, 255, 0.5);
}
.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid #ff9600;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 680px) {
  .level-list {
    gap: 14px;
  }
  .level-icon-wrap {
    width: 52px;
    height: 52px;
  }
  .level-name {
    font-size: 12px;
  }
  .welfare-cards,
  .privilege-list {
    grid-template-columns: 1fr;
  }
  .buy-bar {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
